import { Activity } from '@cube3/common/model/schema/resources/activities';
import {
  useActivityContext,
  useActivityContextWithPath
} from './useActivityContext';
import { useActivityActor } from './useActivityActor';
import { useActivitySubject } from './useActivitySubject';
import {
  getActivityImage,
  getActivityImageType
} from '../utils/getActivityImage';
import { useMemo } from 'react';
import {
  getChangedValue,
  getPattern,
  getPrep,
  getVerb
} from '../utils/getActivityConfig';
import { useActivityClickBehavior } from './useActivityClickBehavior';

export const useProjectActivityFeed = (
  activity: Activity,
  onClose?: () => void
) => {
  const { action, created_at: date } = activity;
  const { subject, actor, related_resources: context } = activity.relationships;

  const { resource: contextResource } = useActivityContext(context);

  const { name: actorName, image: actorAvatar } = useActivityActor(actor);
  const {
    name: subjectName,
    resource: subjectResource,
    shareIntent
  } = useActivitySubject(subject, actor?.id);

  const sourceImage = getActivityImage({
    resource: subjectResource || subject,
    context: contextResource || {},
    action: action
  });

  const { name: contextName, type: contextType } =
    useActivityContextWithPath(activity);

  const { visitObject, visitContext } = useActivityClickBehavior({
    activity,
    onClose
  });

  //#region Generate message

  const imageSection = useMemo(() => {
    const type = getActivityImageType(action, 'project');
    return {
      type: type,
      image: type === 'avatar' ? actorAvatar : sourceImage
    };
  }, [action, actorAvatar, sourceImage]);

  const { topRowSection, bottomRowSection } = useMemo(() => {
    let topRowSection = [];
    let bottomRowSection = [];

    const what = {
      name: activity?.['oldValue'] || subjectName,
      href: undefined,
      onClick: visitObject(true)
    };
    const who = {
      name: actorName
    };
    const where = contextName && {
      name: contextName,
      icon: contextType,
      herf: undefined,
      onClick: visitContext
    };
    const verb = getVerb(activity, shareIntent);
    const prep = getPrep(action);

    bottomRowSection = [date, where && prep, where];
    // {What} was did by {who} at {time} in {where}
    if (getPattern(action, 'project') === 'passive') {
      if (action.toLowerCase().includes('updated')) {
        const { newVal, oldVal } = getChangedValue(activity);
        topRowSection = [
          {
            ...what,
            name: oldVal,
            maxWidth: 120
          },
          'was changed to',
          {
            ...what,
            name: newVal,
            maxWidth: 120
          },
          'by',
          who
        ];
      } else {
        topRowSection = [
          {
            ...what,
            maxWidth: 240
          },
          'was ' + verb,
          who && 'by',
          who
        ];
      }
    }
    // {Who} did {what}, at {time} in {where}
    else {
      switch (action) {
        case 'ContractCreated':
          topRowSection = [
            { ...who, maxWidth: 240 },
            'created contract',
            {
              ...what,
              maxWidth: 240
            },
            'for',
            where
          ];
          bottomRowSection = [date];
          break;
        case 'ContractDeleted':
          topRowSection = [
            { ...who, maxWidth: 240 },
            'deleted',
            {
              ...what,
              maxWidth: 240
            },
            'attached to',
            where
          ];
          bottomRowSection = [date];
          break;
        // TODO: add projectMembers related activities here
        default:
          topRowSection = [{ ...who, maxWidth: 240 }, verb, what];
          break;
      }
    }

    return { topRowSection, bottomRowSection };
  }, [action, contextType, subjectName, contextName]);

  return { imageSection, topRowSection, bottomRowSection };
};
